// ============================================
// INSTANCE AVERAGE TABLE - Organization overview
// ============================================

import React from 'react';
import { ExternalLink } from 'lucide-react';
import { getHebrewName } from '../../constants/muscles';
import type { Employee, RespondentResult, InstanceSummary, CategoryStats } from '../../types/results';

import { getScoreColor } from '../../utils/colors';

interface InstanceAverageTableProps {
    respondents: RespondentResult[];
    employees: Employee[];
    onNavigateToInstance: (instanceId: string) => void;
    allInstances?: InstanceSummary[];  // New SQL-First API provides pre-calculated averages
}

export default function InstanceAverageTable({ respondents, employees, onNavigateToInstance, allInstances }: InstanceAverageTableProps) {
    // Use pre-calculated averages from SQL-First API if available
    const hasPreCalculatedData = allInstances && allInstances.length > 0 && allInstances[0].averages;
    
    if (!hasPreCalculatedData && employees.length === 0) {
        return (
            <div className="analytics-empty-state">
                <p>אין נתונים להצגה</p>
            </div>
        );
    }

    // Get categories from pre-calculated data or employees
    let categories: string[] = [];
    if (hasPreCalculatedData && allInstances![0].averages) {
        categories = Object.keys(allInstances![0].averages);
    } else if (employees.length > 0) {
        categories = Object.keys(employees[0].scores);
    }

    // Build instance data - prefer pre-calculated from SQL-First API
    let instances: {
        id: string;
        name: string;
        respondentCount: number;
        completedCount: number;
        averages: CategoryStats;
    }[] = [];
    
    if (hasPreCalculatedData) {
        // Use pre-calculated data from SQL-First API
        instances = allInstances!.map(inst => ({
            id: inst.id,
            name: inst.name,
            respondentCount: inst.respondentCount,
            completedCount: inst.respondentCount, // Assume all counted are completed
            averages: inst.averages,
        }));
    } else {
        // Legacy: aggregate from employees (fallback)
        const instanceMap: Record<string, {
            id: string;
            name: string;
            respondentCount: number;
            completedCount: number;
            scores: Record<string, { sum: number; count: number }>;
        }> = {};

        const resolveInstanceId = (r: any): string => {
            if (!r) return 'unknown';
            const id = r.instance_id || r.survey_instance_id;
            return id ? String(id).toLowerCase() : 'unknown';
        };

        // Initialize from respondents
        respondents.forEach(r => {
            const instanceId = resolveInstanceId(r);
            const instanceName = r.instance_name || (r as any).survey_name || 'סקר ללא שם';

            if (!instanceMap[instanceId]) {
                instanceMap[instanceId] = {
                    id: instanceId,
                    name: instanceName,
                    respondentCount: 0,
                    completedCount: 0,
                    scores: {}
                };
                categories.forEach(cat => {
                    instanceMap[instanceId].scores[cat] = { sum: 0, count: 0 };
                });
            }
            instanceMap[instanceId].respondentCount += 1;
        });

        // Aggregate scores from employees
        employees.forEach(emp => {
            const respondent = respondents.find(r => String(r.id) === String(emp.id));
            const instanceId = resolveInstanceId(respondent);

            if (instanceMap[instanceId]) {
                instanceMap[instanceId].completedCount += 1;
                categories.forEach(cat => {
                    if (typeof emp.scores[cat] === 'number') {
                        instanceMap[instanceId].scores[cat].sum += emp.scores[cat];
                        instanceMap[instanceId].scores[cat].count += 1;
                    }
                });
            }
        });

        instances = Object.values(instanceMap).map(inst => ({
            id: inst.id,
            name: inst.name,
            respondentCount: inst.respondentCount,
            completedCount: inst.completedCount,
            averages: Object.fromEntries(
                categories.map(cat => [
                    cat,
                    inst.scores[cat].count > 0 ? inst.scores[cat].sum / inst.scores[cat].count : 0
                ])
            ),
        }));
    }

    instances.sort((a, b) => a.name.localeCompare(b.name));

    // Calculate Org-wide Totals
    const orgTotal = {
        respondentCount: 0,
        completedCount: 0,
        averages: {} as CategoryStats,
    };
    
    const totals: Record<string, { sum: number; count: number }> = {};
    categories.forEach(cat => totals[cat] = { sum: 0, count: 0 });

    instances.forEach(inst => {
        orgTotal.respondentCount += inst.respondentCount;
        orgTotal.completedCount += inst.completedCount;
        categories.forEach(cat => {
            const score = inst.averages[cat];
            if (score && score > 0) {
                totals[cat].sum += score * inst.respondentCount; // Weight by respondent count
                totals[cat].count += inst.respondentCount;
            }
        });
    });
    
    orgTotal.averages = Object.fromEntries(
        categories.map(cat => [cat, totals[cat].count > 0 ? totals[cat].sum / totals[cat].count : 0])
    );

    return (
        <div className="analytics-heatmap-container">
            <div
                className="analytics-heatmap-grid"
                style={{
                    gridTemplateColumns: `180px 100px repeat(${categories.length}, 1fr)`,
                }}
            >
                {/* Header Row */}
                <div className="analytics-heatmap-header analytics-heatmap-sticky">קבוצת סקר (קבוצה)</div>
                <div className="analytics-heatmap-header">השתתפות</div>
                {categories.map(cat => (
                    <div
                        key={cat}
                        className="analytics-heatmap-header"
                        title={getHebrewName(cat)}
                    >
                        {getHebrewName(cat)}
                    </div>
                ))}

                {/* Data Rows */}
                {instances.map(inst => (
                    <React.Fragment key={inst.id}>
                        <div
                            className="analytics-heatmap-name analytics-heatmap-sticky"
                            onClick={() => inst.id !== 'unknown' && onNavigateToInstance(inst.id)}
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                cursor: inst.id !== 'unknown' ? 'pointer' : 'default',
                                color: inst.id !== 'unknown' ? 'var(--primary-600)' : 'var(--neutral-400)',
                                backgroundColor: inst.id === 'unknown' ? 'var(--neutral-50)' : 'transparent',
                                fontStyle: inst.id === 'unknown' ? 'italic' : 'normal'
                            }}
                        >
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {inst.id === 'unknown' ? 'משתתפים ללא שיוך לסקר' : inst.name}
                            </span>
                            {inst.id !== 'unknown' && <ExternalLink size={14} style={{ flexShrink: 0, marginLeft: '4px' }} />}
                        </div>
                        <div className="analytics-heatmap-cell" style={{ backgroundColor: 'var(--neutral-50)', fontWeight: 600, fontSize: '12px', color: 'var(--neutral-600)' }}>
                            {inst.respondentCount}
                        </div>
                        {categories.map(cat => {
                            const avg = inst.averages[cat] || 0;
                            const hasValue = avg > 0;
                            return (
                                <div
                                    key={`${inst.id}-${cat}`}
                                    className="analytics-heatmap-cell"
                                    style={{
                                        backgroundColor: hasValue ? getScoreColor(avg) : 'var(--neutral-50)',
                                        color: hasValue ? '#fff' : 'var(--neutral-300)',
                                        fontWeight: hasValue ? 600 : 400
                                    }}
                                >
                                    {hasValue ? avg.toFixed(2) : '-'}
                                </div>
                            );
                        })}
                    </React.Fragment>
                ))}

                {/* Summary Row */}
                <div
                    className="analytics-heatmap-name analytics-heatmap-sticky"
                    style={{ backgroundColor: 'var(--neutral-900)', color: '#fff', fontWeight: 700, borderTop: '2px solid var(--neutral-700)' }}
                >
                    סה"כ ארגוני
                </div>
                <div
                    className="analytics-heatmap-cell"
                    style={{ backgroundColor: 'var(--neutral-800)', color: '#fff', fontWeight: 700, borderTop: '2px solid var(--neutral-700)', fontSize: '13px' }}
                >
                    {orgTotal.respondentCount}
                </div>
                {categories.map(cat => {
                    const avg = orgTotal.averages[cat] || 0;
                    const hasValue = avg > 0;
                    return (
                        <div
                            key={`org-total-${cat}`}
                            className="analytics-heatmap-cell"
                            style={{
                                backgroundColor: 'var(--neutral-800)',
                                color: '#fff',
                                fontWeight: 700,
                                borderTop: '2px solid var(--neutral-700)',
                                fontSize: '14px'
                            }}
                        >
                            {hasValue ? avg.toFixed(2) : '-'}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
